import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Tabs, Tab, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Skeleton, TableSortLabel, CircularProgress,
  IconButton, FormControl, InputLabel, Select, MenuItem, Button, Tooltip,
} from '@mui/material';
import {
  ShowChart as ChartIcon,
  PieChart as PieIcon,
  Security as SecurityIcon,
  TrendingUp as TrendIcon,
  Assessment as AssessIcon,
  Refresh as RefreshIcon,
  Speed as SpeedIcon,
  TrendingDown as TrendDownIcon,
  BarChart as BarChartIcon,
  Insights as InsightsIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer, BarChart, Bar,
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, Cell, ReferenceLine,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { statisticalAPI, moneyFlowAPI } from '../services/api';

/* ═══════════════════════════════════════════════════════════════
   DESIGN TOKENS
   ═══════════════════════════════════════════════════════════════ */
const COLORS = {
  bg: '#f8fafc',
  cardBorder: '#e5e7eb',
  headerBg: '#f8fafc',
  title: '#1e293b',
  subtitle: '#94a3b8',
  textSecondary: '#64748b',
  accent: '#6366f1',
  accentLight: '#818cf8',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  purple: '#8b5cf6',
  blue: '#3b82f6',
  cyan: '#06b6d4',
};

const REGIME_STYLE = {
  LOW:      { bg: '#06b6d4', light: '#ecfeff', text: '#0e7490' },
  NORMAL:   { bg: '#10b981', light: '#f0fdf4', text: '#15803d' },
  ELEVATED: { bg: '#f59e0b', light: '#fffbeb', text: '#a16207' },
  HIGH:     { bg: '#ef4444', light: '#fef2f2', text: '#b91c1c' },
  CRISIS:   { bg: '#a855f7', light: '#faf5ff', text: '#7e22ce' },
};

const PAPER_SX = {
  elevation: 0,
  borderRadius: 1.5,
  border: `1px solid ${COLORS.cardBorder}`,
  overflow: 'hidden',
};

const TH_SX = {
  fontWeight: 700,
  fontSize: '0.68rem',
  color: COLORS.textSecondary,
  py: 1.2,
  px: 1.5,
  bgcolor: COLORS.headerBg,
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  borderBottom: `2px solid ${COLORS.cardBorder}`,
  whiteSpace: 'nowrap',
};

const TD_SX = { py: 1, px: 1.5, fontSize: '0.8rem' };

const BAR_PALETTE = [
  '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4',
  '#ec4899', '#14b8a6', '#f97316', '#3b82f6', '#84cc16', '#e11d48',
  '#0ea5e9', '#a855f7', '#22c55e',
];

// ─── Helpers ─────────────────────────────────────────────────
const fmt = (v, d = 2) => (v != null ? Number(v).toFixed(d) : '—');
const pct = (v, d = 1) => (v != null ? `${Number(v).toFixed(d)}%` : '—');

// ─── Reusable Components ─────────────────────────────────────

/** Hero stat tile */
const StatTile = ({ icon, label, value, sub, color, accent }) => {
  const borderStyle = accent === 'danger'
    ? { border: '1px solid #fecaca', bgcolor: '#fef2f2' }
    : accent === 'warning'
    ? { border: '1px solid #fde68a', bgcolor: '#fffbeb' }
    : accent === 'success'
    ? { border: '1px solid #bbf7d0', bgcolor: '#f0fdf4' }
    : { border: `1px solid ${COLORS.cardBorder}` };

  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, ...borderStyle, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
        {icon && React.cloneElement(icon, { sx: { fontSize: 16, color: color || COLORS.textSecondary } })}
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 28, fontWeight: 800, color: color || COLORS.title, lineHeight: 1 }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 11, color: COLORS.subtitle, mt: 0.5 }}>{sub}</Typography>
      )}
    </Paper>
  );
};

/** Regime badge chip */
const RegimeBadge = ({ regime }) => {
  const { t } = useTranslation();
  const s = REGIME_STYLE[regime] || REGIME_STYLE.NORMAL;
  const label = t(`statistical.garch.${(regime || 'normal').toLowerCase()}`);
  return (
    <Chip size="small" label={label}
      sx={{ bgcolor: s.light, color: s.text, fontWeight: 700, fontSize: '0.65rem', height: 22, borderRadius: '6px', border: `1px solid ${s.bg}33` }} />
  );
};

// ─── Sortable table hook ─────────────────────────────────────
const useSortable = (data, defaultKey, defaultDir = 'desc') => {
  const [orderBy, setOrderBy] = useState(defaultKey);
  const [order, setOrder] = useState(defaultDir);
  const handleSort = (key) => {
    setOrder(orderBy === key && order === 'desc' ? 'asc' : 'desc');
    setOrderBy(key);
  };
  const sorted = useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => {
      let va = a[orderBy], vb = b[orderBy];
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va == null) return 1;
      if (vb == null) return -1;
      return order === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });
  }, [data, orderBy, order]);
  return { sorted, orderBy, order, handleSort };
};

const SortHeader = ({ field, label, orderBy, order, onSort, align = 'right' }) => (
  <TableCell align={align} sx={TH_SX}>
    <TableSortLabel active={orderBy === field} direction={orderBy === field ? order : 'desc'} onClick={() => onSort(field)}>
      {label}
    </TableSortLabel>
  </TableCell>
);

// ─── Page Skeleton ───────────────────────────────────────────
const PageSkeleton = () => (
  <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: '12px' }} />
        <Box>
          <Skeleton width={260} height={26} />
          <Skeleton width={200} height={14} sx={{ mt: 0.5 }} />
        </Box>
      </Box>
      <Skeleton variant="rounded" width={90} height={36} sx={{ borderRadius: '10px' }} />
    </Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
      {[0, 1, 2, 3].map(i => (
        <Paper key={i} elevation={0} sx={{ p: 2, borderRadius: 1.5, border: `1px solid ${COLORS.cardBorder}` }}>
          <Skeleton width={80} height={12} sx={{ mb: 1 }} />
          <Skeleton width={60} height={32} />
          <Skeleton width={100} height={12} sx={{ mt: 0.5 }} />
        </Paper>
      ))}
    </Box>
    <Skeleton variant="rounded" width="100%" height={48} sx={{ borderRadius: 1.5, mb: 2.5 }} />
    <Paper elevation={0} sx={{ borderRadius: 1.5, border: `1px solid ${COLORS.cardBorder}`, overflow: 'hidden' }}>
      <Box sx={{ px: 2.5, py: 1.5, bgcolor: COLORS.headerBg, borderBottom: `1px solid ${COLORS.cardBorder}` }}>
        <Skeleton width={180} height={18} />
      </Box>
      {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
        <Box key={i} sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${COLORS.cardBorder}`, display: 'flex', gap: 3 }}>
          <Skeleton width={48} height={16} />
          <Skeleton width={60} height={16} />
          <Skeleton width={60} height={16} />
          <Skeleton width={60} height={16} />
          <Skeleton width={60} height={16} />
        </Box>
      ))}
    </Paper>
  </Box>
);

// ─── Table Skeleton Rows ─────────────────────────────────────
const SkeletonRows = ({ cols = 9, rows = 8 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <TableRow key={i} sx={{ bgcolor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
        <TableCell sx={TD_SX}>
          <Skeleton width={42} height={14} />
          <Skeleton width={90} height={10} sx={{ mt: 0.5 }} />
        </TableCell>
        {Array.from({ length: cols - 1 }).map((_, j) => (
          <TableCell key={j} align="right" sx={TD_SX}>
            <Skeleton width={48 + (j % 3) * 8} height={14} sx={{ ml: 'auto' }} />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

// ─── GARCH Tab ───────────────────────────────────────────────
const GarchTab = ({ t, tickers, loading: parentLoading, garchData, garchLoading, onRefresh, nameMap }) => {
  const { sorted, orderBy, order, handleSort } = useSortable(garchData, 'vol_ratio');
  const isLoading = garchLoading || parentLoading;

  return (
    <Box>
      {/* GARCH table */}
      <Paper {...PAPER_SX}>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: COLORS.headerBg, borderBottom: `1px solid ${COLORS.cardBorder}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChartIcon sx={{ fontSize: 16, color: COLORS.accent }} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: COLORS.title }}>
              {t('statistical.garch.title')}
            </Typography>
            <Chip size="small" label={t('statistical.garch.nStocks', { count: garchData.length })}
              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: `${COLORS.accent}14`, color: COLORS.accent }} />
          </Box>
          <Tooltip title={t('statistical.garch.refreshGarch')}>
            <IconButton size="small" onClick={onRefresh} disabled={isLoading}
              sx={{ border: `1px solid ${COLORS.cardBorder}`, borderRadius: 1, width: 32, height: 32,
                '&:hover': { borderColor: COLORS.accent, bgcolor: '#eff6ff' } }}>
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortHeader field="ticker" label={t('statistical.common.ticker')} orderBy={orderBy} order={order} onSort={handleSort} align="left" />
                <SortHeader field="vol_regime" label={t('statistical.garch.regime')} orderBy={orderBy} order={order} onSort={handleSort} />
                <SortHeader field="vol_ratio" label={t('statistical.garch.volRatio')} orderBy={orderBy} order={order} onSort={handleSort} />
                <SortHeader field="current_cond_vol_pct" label={t('statistical.garch.condVolShort')} orderBy={orderBy} order={order} onSort={handleSort} />
                <SortHeader field="annualized_cond_vol_pct" label={t('statistical.garch.annualVol')} orderBy={orderBy} order={order} onSort={handleSort} />
                <SortHeader field="long_run_vol_pct" label={t('statistical.garch.longRun')} orderBy={orderBy} order={order} onSort={handleSort} />
                <SortHeader field="forecast_1d_pct" label={t('statistical.garch.forecast1dShort')} orderBy={orderBy} order={order} onSort={handleSort} />
                <SortHeader field="forecast_5d_pct" label={t('statistical.garch.forecast5dShort')} orderBy={orderBy} order={order} onSort={handleSort} />
                <SortHeader field="persistence" label={t('statistical.garch.persistShort')} orderBy={orderBy} order={order} onSort={handleSort} />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? <SkeletonRows cols={9} rows={8} /> : (<>
              {sorted.map((r, idx) => (
                <TableRow key={r.ticker} hover sx={{ bgcolor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <TableCell sx={{ ...TD_SX, fontWeight: 700, color: COLORS.title }}>
                    {r.ticker}
                    {nameMap?.[r.ticker] && <Typography sx={{ fontSize: '0.55rem', color: COLORS.subtitle, fontWeight: 400, lineHeight: 1.2, mt: 0.2 }}>{nameMap[r.ticker]}</Typography>}
                  </TableCell>
                  <TableCell align="right" sx={TD_SX}><RegimeBadge regime={r.vol_regime} /></TableCell>
                  <TableCell align="right" sx={{ ...TD_SX, fontWeight: 700, color: r.vol_ratio > 1.3 ? COLORS.danger : r.vol_ratio > 1.0 ? COLORS.warning : COLORS.success }}>
                    {fmt(r.vol_ratio)}
                  </TableCell>
                  <TableCell align="right" sx={TD_SX}>{pct(r.current_cond_vol_pct)}</TableCell>
                  <TableCell align="right" sx={TD_SX}>{pct(r.annualized_cond_vol_pct)}</TableCell>
                  <TableCell align="right" sx={TD_SX}>{pct(r.long_run_vol_pct)}</TableCell>
                  <TableCell align="right" sx={{ ...TD_SX, color: COLORS.blue, fontWeight: 600 }}>{pct(r.forecast_1d_pct)}</TableCell>
                  <TableCell align="right" sx={{ ...TD_SX, color: COLORS.blue, fontWeight: 600 }}>{pct(r.forecast_5d_pct)}</TableCell>
                  <TableCell align="right" sx={TD_SX}>{fmt(r.persistence, 3)}</TableCell>
                </TableRow>
              ))}
              {sorted.length === 0 && (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: COLORS.subtitle }}>{t('statistical.common.noData')}</TableCell></TableRow>
              )}
              </>)}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

// ─── VaR / Kelly Tab ─────────────────────────────────────────
const VarTab = ({ t, tickers, loading: parentLoading, varData, varLoading, onRefresh, nameMap }) => {
  const { sorted, orderBy, order, handleSort } = useSortable(varData, 'var_pct', 'asc');
  const isLoading = varLoading || parentLoading;

  return (
    <Box>
      <Paper {...PAPER_SX}>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: COLORS.headerBg, borderBottom: `1px solid ${COLORS.cardBorder}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon sx={{ fontSize: 16, color: COLORS.danger }} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: COLORS.title }}>
              {t('statistical.var.title')}
            </Typography>
            <Chip size="small" label={t('statistical.var.nStocks', { count: varData.length })}
              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: `${COLORS.danger}14`, color: COLORS.danger }} />
          </Box>
          <Tooltip title={t('statistical.var.refreshVar')}>
            <IconButton size="small" onClick={onRefresh} disabled={isLoading}
              sx={{ border: `1px solid ${COLORS.cardBorder}`, borderRadius: 1, width: 32, height: 32,
                '&:hover': { borderColor: COLORS.accent, bgcolor: '#eff6ff' } }}>
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortHeader field="ticker" label={t('statistical.common.ticker')} orderBy={orderBy} order={order} onSort={handleSort} align="left" />
                <SortHeader field="var_pct" label={t('statistical.var.var95')} orderBy={orderBy} order={order} onSort={handleSort} />
                <SortHeader field="es_pct" label={t('statistical.var.esCvar')} orderBy={orderBy} order={order} onSort={handleSort} />
                <SortHeader field="daily_vol" label={t('statistical.var.dailyVol')} orderBy={orderBy} order={order} onSort={handleSort} />
                <SortHeader field="annualized_vol" label={t('statistical.var.annualVol')} orderBy={orderBy} order={order} onSort={handleSort} />
                <SortHeader field="win_rate" label={t('statistical.var.winRate')} orderBy={orderBy} order={order} onSort={handleSort} />
                <SortHeader field="edge_pct" label={t('statistical.var.edge')} orderBy={orderBy} order={order} onSort={handleSort} />
                <SortHeader field="half_kelly_pct" label={t('statistical.var.halfKelly')} orderBy={orderBy} order={order} onSort={handleSort} />
                <SortHeader field="payoff_ratio" label={t('statistical.var.payoff')} orderBy={orderBy} order={order} onSort={handleSort} />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? <SkeletonRows cols={9} rows={8} /> : (<>
              {sorted.map((r, idx) => (
                <TableRow key={r.ticker} hover sx={{ bgcolor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <TableCell sx={{ ...TD_SX, fontWeight: 700, color: COLORS.title }}>
                    {r.ticker}
                    {nameMap?.[r.ticker] && <Typography sx={{ fontSize: '0.55rem', color: COLORS.subtitle, fontWeight: 400, lineHeight: 1.2, mt: 0.2 }}>{nameMap[r.ticker]}</Typography>}
                  </TableCell>
                  <TableCell align="right" sx={{ ...TD_SX, fontWeight: 700, color: COLORS.danger }}>{pct(r.var_pct)}</TableCell>
                  <TableCell align="right" sx={{ ...TD_SX, color: COLORS.purple }}>{pct(r.es_pct)}</TableCell>
                  <TableCell align="right" sx={TD_SX}>{pct(r.daily_vol)}</TableCell>
                  <TableCell align="right" sx={TD_SX}>{pct(r.annualized_vol)}</TableCell>
                  <TableCell align="right" sx={{ ...TD_SX, color: r.win_rate > 0.5 ? COLORS.success : COLORS.danger }}>
                    {r.win_rate != null ? pct(r.win_rate * 100) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ ...TD_SX, fontWeight: 700, color: r.edge_pct > 0 ? COLORS.success : COLORS.danger }}>
                    {r.edge_pct > 0 ? '+' : ''}{pct(r.edge_pct)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...TD_SX, color: COLORS.blue }}>{pct(r.half_kelly_pct)}</TableCell>
                  <TableCell align="right" sx={TD_SX}>{fmt(r.payoff_ratio)}</TableCell>
                </TableRow>
              ))}
              {sorted.length === 0 && (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: COLORS.subtitle }}>{t('statistical.common.noData')}</TableCell></TableRow>
              )}
              </>)}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

// ─── Markowitz Tab ───────────────────────────────────────────
const MarkowitzTab = ({ t, tickers, loading: parentLoading, onMetrics }) => {
  const [result, setResult] = useState(null);
  const [frontier, setFrontier] = useState(null);
  const [method, setMethod] = useState('tangency');
  const [loading, setLoading] = useState(false);

  const optimize = useCallback(async () => {
    if (!tickers?.length || tickers.length < 2) return;
    setLoading(true);
    try {
      const maxW = Math.max(0.15, 1.1 / tickers.length);
      const [optRes, frontRes] = await Promise.allSettled([
        statisticalAPI.optimizePortfolio(tickers, method, 252, maxW),
        statisticalAPI.getEfficientFrontier(tickers, 12, 252, maxW),
      ]);
      if (optRes.status === 'fulfilled') {
        setResult(optRes.value);
        onMetrics?.(optRes.value);
      }
      if (frontRes.status === 'fulfilled') setFrontier(frontRes.value);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [method, onMetrics, tickers]);

  useEffect(() => { optimize(); }, [optimize]);

  const weightData = result?.weights
    ? Object.entries(result.weights).filter(([, w]) => w > 0.001).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ name: k, weight: v * 100 }))
    : [];
  const sectorData = result?.sector_weights
    ? Object.entries(result.sector_weights).filter(([, w]) => w > 0.001).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ name: k.replace(/_/g, ' '), weight: v * 100 }))
    : [];

  const isLoading = loading || parentLoading;

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel sx={{ fontSize: '0.82rem' }}>{t('statistical.markowitz.method')}</InputLabel>
          <Select value={method} onChange={(e) => setMethod(e.target.value)} label={t('statistical.markowitz.method')}
            sx={{ borderRadius: 1, fontSize: '0.82rem', '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.cardBorder } }}>
            <MenuItem value="tangency">{t('statistical.markowitz.tangency')}</MenuItem>
            <MenuItem value="min_variance">{t('statistical.markowitz.minVariance')}</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={optimize} disabled={isLoading} startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
          sx={{ borderRadius: 1, borderColor: COLORS.cardBorder, color: COLORS.textSecondary, fontWeight: 600, fontSize: '0.78rem', textTransform: 'none', height: 38,
            '&:hover': { borderColor: COLORS.accent, color: COLORS.accent, bgcolor: '#eff6ff' } }}>
          {t('statistical.markowitz.optimize')}
        </Button>
        <Typography sx={{ fontSize: '0.75rem', color: COLORS.subtitle, ml: 'auto' }}>
          {tickers?.length || 0} {t('statistical.common.ticker').toLowerCase()} {t('statistical.common.fromRecommendations')}
        </Typography>
      </Box>

      {isLoading && (
        <Paper {...PAPER_SX} sx={{ mb: 2 }}>
          <Box sx={{ px: 2.5, py: 1.5, bgcolor: COLORS.headerBg, borderBottom: `1px solid ${COLORS.cardBorder}` }}>
            <Skeleton width={200} height={18} />
          </Box>
          {[0, 1, 2, 3, 4].map(i => (
            <Box key={i} sx={{ px: 2.5, py: 1.8, borderBottom: `1px solid ${COLORS.cardBorder}`, display: 'flex', gap: 3 }}>
              <Skeleton width={50} height={16} />
              <Skeleton width={70} height={16} />
              <Skeleton width={60} height={16} />
            </Box>
          ))}
        </Paper>
      )}

      {!result && !isLoading && (
        <Paper {...PAPER_SX}>
          <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <PieIcon sx={{ fontSize: 48, color: COLORS.cardBorder }} />
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.title }}>
              {t('statistical.markowitz.title')}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: COLORS.textSecondary, textAlign: 'center', maxWidth: 360, lineHeight: 1.6 }}>
              {t('statistical.markowitz.emptyDesc')}
            </Typography>
            <Button variant="outlined" onClick={optimize} startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
              sx={{ borderRadius: 1, borderColor: COLORS.accent, color: COLORS.accent, fontWeight: 600, fontSize: '0.78rem', textTransform: 'none',
                '&:hover': { bgcolor: '#eff6ff' } }}>
              {t('statistical.markowitz.optimize')}
            </Button>
          </Box>
        </Paper>
      )}

      {result && !isLoading && (
        <>
          {/* Charts row */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' }, gap: 2, mb: 2.5 }}>
            {/* Weight chart */}
            <Paper {...PAPER_SX}>
              <Box sx={{ px: 2.5, py: 1.25, bgcolor: COLORS.headerBg, borderBottom: `1px solid ${COLORS.cardBorder}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                <BarChartIcon sx={{ fontSize: 15, color: COLORS.accent }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: COLORS.title }}>{t('statistical.markowitz.weights')}</Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                <ResponsiveContainer width="100%" height={Math.max(220, weightData.length * 28)}>
                  <BarChart data={weightData} layout="vertical" margin={{ left: 5, right: 20, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tickFormatter={(v) => `${v.toFixed(0)}%`} tick={{ fontSize: 11, fill: COLORS.textSecondary }} />
                    <YAxis type="category" dataKey="name" width={48} tick={{ fontSize: 11, fontWeight: 600, fill: COLORS.title }} />
                    <RTooltip formatter={(v) => `${v.toFixed(1)}%`} contentStyle={{ borderRadius: 8, fontSize: 12, border: `1px solid ${COLORS.cardBorder}` }} />
                    <Bar dataKey="weight" radius={[0, 4, 4, 0]}>
                      {weightData.map((_, i) => <Cell key={i} fill={BAR_PALETTE[i % BAR_PALETTE.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>

            {/* Sector allocation */}
            <Paper {...PAPER_SX}>
              <Box sx={{ px: 2.5, py: 1.25, bgcolor: COLORS.headerBg, borderBottom: `1px solid ${COLORS.cardBorder}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PieIcon sx={{ fontSize: 15, color: COLORS.purple }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: COLORS.title }}>{t('statistical.markowitz.sectorWeights')}</Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                <ResponsiveContainer width="100%" height={Math.max(220, sectorData.length * 32)}>
                  <BarChart data={sectorData} layout="vertical" margin={{ left: 15, right: 20, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tickFormatter={(v) => `${v.toFixed(0)}%`} tick={{ fontSize: 11, fill: COLORS.textSecondary }} />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10, fill: COLORS.title }} />
                    <RTooltip formatter={(v) => `${v.toFixed(1)}%`} contentStyle={{ borderRadius: 8, fontSize: 12, border: `1px solid ${COLORS.cardBorder}` }} />
                    <Bar dataKey="weight" fill={COLORS.purple} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Box>
        </>
      )}

      {/* Efficient frontier */}
      {frontier?.frontier && (
        <Paper {...PAPER_SX}>
          <Box sx={{ px: 2.5, py: 1.25, bgcolor: COLORS.headerBg, borderBottom: `1px solid ${COLORS.cardBorder}`, display: 'flex', alignItems: 'center', gap: 1 }}>
            <InsightsIcon sx={{ fontSize: 15, color: COLORS.blue }} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: COLORS.title }}>{t('statistical.markowitz.frontier')}</Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <ResponsiveContainer width="100%" height={300}>
              <ScatterChart margin={{ top: 10, right: 30, bottom: 25, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" dataKey="vol" name="Volatility"
                  tickFormatter={(v) => `${v.toFixed(1)}%`} tick={{ fontSize: 11, fill: COLORS.textSecondary }}
                  label={{ value: t('statistical.markowitz.volPct'), position: 'bottom', offset: -5, style: { fontSize: 11, fill: COLORS.textSecondary } }} />
                <YAxis type="number" dataKey="ret" name="Return"
                  tickFormatter={(v) => `${v.toFixed(1)}%`} tick={{ fontSize: 11, fill: COLORS.textSecondary }}
                  label={{ value: t('statistical.markowitz.returnPct'), angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: COLORS.textSecondary } }} />
                <RTooltip formatter={(v) => `${v.toFixed(2)}%`} contentStyle={{ borderRadius: 8, fontSize: 12, border: `1px solid ${COLORS.cardBorder}` }} />
                <ReferenceLine y={0} stroke={COLORS.subtitle} strokeDasharray="3 3" />
                <Scatter
                  data={frontier.frontier.map((p) => ({ vol: p.volatility_pct, ret: p.return_pct, sharpe: p.sharpe_ratio }))}
                  fill={COLORS.accent} line={{ stroke: COLORS.accent, strokeWidth: 2 }}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

// ─── Calibration Tab ─────────────────────────────────────────
const CalibrationTab = ({ t, overview, calLoading }) => {
  const BUCKET_COLORS = [COLORS.danger, COLORS.warning, '#eab308', '#84cc16', COLORS.success, COLORS.blue];

  return (
    <Box>
      {calLoading && (
        <Paper {...PAPER_SX}>
          <Box sx={{ px: 2.5, py: 1.5, bgcolor: COLORS.headerBg, borderBottom: `1px solid ${COLORS.cardBorder}` }}>
            <Skeleton width={180} height={18} />
          </Box>
          {[0, 1, 2, 3].map(i => (
            <Box key={i} sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${COLORS.cardBorder}`, display: 'flex', gap: 3 }}>
              <Skeleton width={80} height={16} />
              <Skeleton width={100} height={16} />
              <Skeleton width={60} height={16} />
              <Skeleton width={60} height={16} />
            </Box>
          ))}
        </Paper>
      )}

      {!overview && !calLoading && (
        <Paper {...PAPER_SX}>
          <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <BarChartIcon sx={{ fontSize: 48, color: COLORS.cardBorder }} />
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.title }}>
              {t('statistical.calibration.title')}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: COLORS.textSecondary, textAlign: 'center', maxWidth: 360, lineHeight: 1.6 }}>
              {t('statistical.calibration.emptyDesc')}
            </Typography>
          </Box>
        </Paper>
      )}

      {overview && !calLoading && (
        <>
          {/* Buckets chart */}
          <Paper {...PAPER_SX} sx={{ ...PAPER_SX, mb: 2.5 }}>
            <Box sx={{ px: 2.5, py: 1.25, bgcolor: COLORS.headerBg, borderBottom: `1px solid ${COLORS.cardBorder}`, display: 'flex', alignItems: 'center', gap: 1 }}>
              <BarChartIcon sx={{ fontSize: 15, color: COLORS.success }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: COLORS.title }}>{t('statistical.calibration.title')}</Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={overview.buckets} margin={{ top: 10, right: 30, bottom: 5, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="score_range" tick={{ fontSize: 11, fill: COLORS.textSecondary }} />
                  <YAxis tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} domain={[0, 1]} tick={{ fontSize: 11, fill: COLORS.textSecondary }} />
                  <RTooltip formatter={(v) => `${(v * 100).toFixed(1)}%`} contentStyle={{ borderRadius: 8, fontSize: 12, border: `1px solid ${COLORS.cardBorder}` }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="win_rate" name={t('statistical.calibration.winRate')} fill={COLORS.success} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="default_prob" name={t('statistical.calibration.defaultProb')} fill={COLORS.blue} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Buckets table */}
          <Paper {...PAPER_SX}>
            <Box sx={{ px: 2.5, py: 1.25, bgcolor: COLORS.headerBg, borderBottom: `1px solid ${COLORS.cardBorder}`, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessIcon sx={{ fontSize: 15, color: COLORS.accent }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: COLORS.title }}>{t('statistical.calibration.buckets')}</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={TH_SX}>{t('statistical.calibration.bucket')}</TableCell>
                    <TableCell align="right" sx={TH_SX}>{t('statistical.calibration.totalTrades')}</TableCell>
                    <TableCell align="right" sx={TH_SX}>{t('statistical.calibration.winRate')}</TableCell>
                    <TableCell align="right" sx={TH_SX}>{t('statistical.calibration.defaultProb')}</TableCell>
                    <TableCell align="right" sx={TH_SX}>{t('statistical.calibration.confidence')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {overview.buckets.map((b, i) => (
                    <TableRow key={b.score_range} hover sx={{ bgcolor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <TableCell sx={TD_SX}>
                        <Chip size="small" label={b.score_range}
                          sx={{ bgcolor: BUCKET_COLORS[i] + '14', color: BUCKET_COLORS[i], fontWeight: 700, fontSize: '0.68rem', height: 22, borderRadius: '6px' }} />
                      </TableCell>
                      <TableCell align="right" sx={TD_SX}>{b.total_trades}</TableCell>
                      <TableCell align="right" sx={{ ...TD_SX, fontWeight: 700, color: b.win_rate > 0.5 ? COLORS.success : COLORS.danger }}>
                        {pct(b.win_rate * 100)}
                      </TableCell>
                      <TableCell align="right" sx={TD_SX}>{pct(b.default_prob * 100)}</TableCell>
                      <TableCell align="right" sx={TD_SX}>
                        <Chip size="small" label={b.confidence}
                          sx={{
                            height: 20, fontSize: '0.62rem', fontWeight: 700, borderRadius: '6px',
                            bgcolor: b.confidence === 'HIGH' ? '#f0fdf4' : b.confidence === 'MEDIUM' ? '#fffbeb' : '#f1f5f9',
                            color: b.confidence === 'HIGH' ? COLORS.success : b.confidence === 'MEDIUM' ? COLORS.warning : COLORS.textSecondary,
                            border: `1px solid ${b.confidence === 'HIGH' ? COLORS.success : b.confidence === 'MEDIUM' ? COLORS.warning : '#cbd5e1'}33`,
                          }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

// ─── Main Page ───────────────────────────────────────────────
const StatisticalDashboard = () => {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState(0);
  const [tickers, setTickers] = useState([]);
  const [recData, setRecData] = useState([]);
  const [tradeDate, setTradeDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [garchData, setGarchData] = useState([]);
  const [market, setMarket] = useState(null);
  const [garchLoading, setGarchLoading] = useState(false);
  const [varData, setVarData] = useState([]);
  const [varLoading, setVarLoading] = useState(false);
  const [calOverview, setCalOverview] = useState(null);
  const [calLoading, setCalLoading] = useState(false);
  const [markowitzMetrics, setMarkowitzMetrics] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await moneyFlowAPI.getRecommendations();
        const data = res?.data || res;
        const list = data?.tickers || [];
        setRecData(list);
        setTickers(list.map(r => r.ticker));
        if (data?.date) setTradeDate(data.date);
      } catch (e) { console.error('Failed to load recommendations:', e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  // GARCH scan — lifted so hero tiles render at page top
  const garchScan = useCallback(async () => {
    if (!tickers?.length) return;
    setGarchLoading(true);
    try {
      const [batchRes, mktRes] = await Promise.allSettled([
        statisticalAPI.getGarchBatch(tickers),
        statisticalAPI.getMarketRegime(),
      ]);
      if (batchRes.status === 'fulfilled') {
        setGarchData(Object.values(batchRes.value).filter(v => v && v.ticker));
      }
      if (mktRes.status === 'fulfilled') setMarket(mktRes.value);
    } catch (e) { console.error(e); }
    finally { setGarchLoading(false); }
  }, [tickers]);

  useEffect(() => { garchScan(); }, [garchScan]);

  // VaR scan — lifted so hero tiles render at page top
  const varScan = useCallback(async () => {
    if (!tickers?.length) return;
    setVarLoading(true);
    try {
      const results = await Promise.allSettled(
        tickers.map(async (tk) => {
          const [varRes, kellyRes] = await Promise.allSettled([
            statisticalAPI.getVaR(tk),
            statisticalAPI.getKelly(tk),
          ]);
          return {
            ticker: tk,
            ...(varRes.status === 'fulfilled' ? varRes.value : {}),
            ...(kellyRes.status === 'fulfilled' ? {
              win_rate: kellyRes.value.win_rate,
              edge_pct: kellyRes.value.edge_pct,
              half_kelly_pct: kellyRes.value.half_kelly_pct,
              payoff_ratio: kellyRes.value.payoff_ratio,
            } : {}),
          };
        })
      );
      setVarData(results.filter(r => r.status === 'fulfilled').map(r => r.value));
    } catch (e) { console.error(e); }
    finally { setVarLoading(false); }
  }, [tickers]);

  useEffect(() => { varScan(); }, [varScan]);

  // Calibration fetch — lifted so hero tiles render at page top
  const calFetch = useCallback(async () => {
    setCalLoading(true);
    try { setCalOverview(await statisticalAPI.getCalibrationOverview()); }
    catch (e) { console.error(e); }
    finally { setCalLoading(false); }
  }, []);

  useEffect(() => { calFetch(); }, [calFetch]);

  // Refresh: re-fetch recommendations then re-scan all
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await moneyFlowAPI.getRecommendations();
      const data = res?.data || res;
      const list = data?.tickers || [];
      setRecData(list);
      setTickers(list.map(r => r.ticker));
      if (data?.date) setTradeDate(data.date);
    } catch (e) { console.error(e); }
    finally { setRefreshing(false); }
  }, []);

  // Build ticker → company_name lookup from recommendation data
  const nameMap = useMemo(() => {
    const m = {};
    recData.forEach(r => { if (r.company_name) m[r.ticker] = r.company_name; });
    return m;
  }, [recData]);

  // Rescan: re-run all analysis engines (GARCH + VaR + Calibration)
  const handleRescan = useCallback(async () => {
    setScanning(true);
    try {
      await Promise.allSettled([garchScan(), varScan(), calFetch()]);
    } finally { setScanning(false); }
  }, [garchScan, varScan, calFetch]);

  const isAnyLoading = refreshing || scanning || garchLoading || varLoading || calLoading;

  const highVolCount = garchData.filter(r => r.vol_regime === 'HIGH' || r.vol_regime === 'CRISIS').length;
  const avgPersistence = garchData.length > 0
    ? (garchData.reduce((s, r) => s + (r.persistence || 0), 0) / garchData.length)
    : 0;
  const avgVaR = varData.length > 0 ? varData.reduce((s, r) => s + (r.var_pct || 0), 0) / varData.length : 0;
  const avgEdge = varData.length > 0 ? varData.reduce((s, r) => s + (r.edge_pct || 0), 0) / varData.length : 0;
  const positiveEdge = varData.filter(r => r.edge_pct > 0).length;

  if (loading) return <PageSkeleton />;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* ═══ Header ═══ */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: '12px',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 3px 12px rgba(99,102,241,0.3)',
          }}>
            <AssessIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: COLORS.title, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                {t('statistical.title')}
              </Typography>
              {tradeDate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: COLORS.accent, boxShadow: `0 0 6px ${COLORS.accent}` }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                    {tradeDate}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: COLORS.subtitle }}>
              {t('statistical.subtitle')} — {t('statistical.common.stocksAutoScanned', { count: tickers.length })}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={scanning ? <CircularProgress size={16} color="inherit" /> : <InsightsIcon sx={{ fontSize: 16 }} />}
            onClick={handleRescan}
            disabled={isAnyLoading}
            sx={{ textTransform: 'none', fontSize: '0.78rem', fontWeight: 600, borderRadius: 1, minHeight: 34,
              bgcolor: COLORS.accent, '&:hover': { bgcolor: '#4f46e5' },
              '& .MuiButton-startIcon': { mr: 0.5 } }}
          >
            {scanning ? t('statistical.common.scanning') : t('statistical.common.rescanAll')}
          </Button>
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={handleRefresh}
            disabled={isAnyLoading}
            sx={{ textTransform: 'none', fontSize: '0.78rem', fontWeight: 600, borderRadius: 1, minHeight: 34,
              color: COLORS.textSecondary, borderColor: COLORS.cardBorder,
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
              '& .MuiButton-startIcon': { mr: 0.5 } }}
          >
            {refreshing ? t('statistical.common.loadingData') : t('common.refresh')}
          </Button>
        </Box>
      </Box>

      {/* ═══ Hero Tiles (dynamic by tab) ═══ */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 2.5 }}>
        {tab === 0 && <>
          <StatTile icon={<SpeedIcon />} label={t('statistical.garch.marketRegime')}
            value={market?.regime || '—'} color={REGIME_STYLE[market?.regime]?.text || COLORS.title}
            accent={market?.regime === 'HIGH' || market?.regime === 'CRISIS' ? 'danger' : market?.regime === 'ELEVATED' ? 'warning' : 'success'}
            sub={market?.vol_trend || ''} />
          <StatTile icon={<ChartIcon />} label={t('statistical.garch.currentVol')} value={pct(market?.current_vol_pct)} color={COLORS.blue} />
          <StatTile icon={<InsightsIcon />} label={t('statistical.garch.persistence')} value={fmt(avgPersistence, 3)}
            sub={t('statistical.garch.stocksAvg', { count: garchData.length })} color={COLORS.purple} />
          <StatTile icon={<TrendDownIcon />} label={t('statistical.garch.highVolStocks')} value={highVolCount}
            sub={t('statistical.garch.ofScanned', { count: garchData.length })} color={highVolCount > 5 ? COLORS.danger : COLORS.success}
            accent={highVolCount > 5 ? 'danger' : undefined} />
        </>}
        {tab === 1 && <>
          <StatTile icon={<SecurityIcon />} label={t('statistical.var.avgVaR')} value={pct(avgVaR)} color={COLORS.danger} accent="danger" />
          <StatTile icon={<TrendIcon />} label={t('statistical.var.avgEdge')} value={pct(avgEdge)} color={avgEdge > 0 ? COLORS.success : COLORS.danger} accent={avgEdge > 0 ? 'success' : 'danger'} />
          <StatTile icon={<BarChartIcon />} label={t('statistical.var.positiveEdge')} value={positiveEdge} sub={t('statistical.var.ofStocks', { count: varData.length })} color={COLORS.success} />
          <StatTile icon={<AssessIcon />} label={t('statistical.var.stocksScanned')} value={varData.length} sub={t('statistical.var.varKellyCombined')} color={COLORS.blue} />
        </>}
        {tab === 2 && <>
          <StatTile icon={<TrendIcon />} label={t('statistical.markowitz.expectedReturn')} value={pct(markowitzMetrics?.expected_return_pct)}
            color={markowitzMetrics?.expected_return_pct > 0 ? COLORS.success : COLORS.danger}
            accent={markowitzMetrics?.expected_return_pct > 0 ? 'success' : 'danger'} />
          <StatTile icon={<SpeedIcon />} label={t('statistical.markowitz.volatility')} value={pct(markowitzMetrics?.volatility_pct)} color={COLORS.warning} />
          <StatTile icon={<InsightsIcon />} label={t('statistical.markowitz.sharpeRatio')} value={fmt(markowitzMetrics?.sharpe_ratio, 3)}
            color={markowitzMetrics?.sharpe_ratio > 0.5 ? COLORS.success : markowitzMetrics?.sharpe_ratio > 0 ? COLORS.warning : COLORS.danger}
            accent={markowitzMetrics?.sharpe_ratio > 0.5 ? 'success' : markowitzMetrics?.sharpe_ratio > 0 ? 'warning' : 'danger'} />
          <StatTile icon={<AssessIcon />} label={t('statistical.markowitz.tickersUsed')} value={tickers.length} sub={t('statistical.common.fromRecommendations')} color={COLORS.accent} />
        </>}
        {tab === 3 && calOverview && <>
          <StatTile icon={<AssessIcon />} label={t('statistical.calibration.quality')} value={calOverview.calibration_quality}
            color={calOverview.calibration_quality === 'GOOD' ? COLORS.success : calOverview.calibration_quality === 'FAIR' ? COLORS.warning : COLORS.danger}
            accent={calOverview.calibration_quality === 'GOOD' ? 'success' : calOverview.calibration_quality === 'FAIR' ? 'warning' : 'danger'} />
          <StatTile icon={<BarChartIcon />} label={t('statistical.calibration.totalTrades')} value={calOverview.total_trades} color={COLORS.blue} />
          <StatTile icon={<InsightsIcon />} label={t('statistical.calibration.strategy')} value={calOverview.strategy_name || '—'} color={COLORS.accent} />
          <StatTile icon={<SpeedIcon />} label={t('statistical.calibration.lastUpdated')}
            value={calOverview.last_updated ? new Date(calOverview.last_updated).toLocaleDateString() : '—'} color={COLORS.textSecondary} />
        </>}
      </Box>

      {/* ═══ Recommendation Insights ═══ */}
      {recData.length > 0 && (
        <Paper elevation={0} sx={{ borderRadius: 1.5, border: `1px solid ${COLORS.cardBorder}`, mb: 2.5, overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, py: 1.25, bgcolor: COLORS.headerBg, borderBottom: `1px solid ${COLORS.cardBorder}`, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendIcon sx={{ fontSize: 16, color: COLORS.accent }} />
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {t('statistical.recommendations.title')}
            </Typography>
            <Chip size="small" label={recData.length}
              sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: `${COLORS.accent}14`, color: COLORS.accent }} />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...TH_SX, position: 'sticky', left: 0, zIndex: 3 }}>{t('statistical.common.ticker')}</TableCell>
                  <TableCell align="center" sx={TH_SX}>{t('statistical.recommendations.action')}</TableCell>
                  <TableCell align="right" sx={TH_SX}>{t('statistical.recommendations.score')}</TableCell>
                  <TableCell align="right" sx={TH_SX}>{t('statistical.recommendations.winProb')}</TableCell>
                  <TableCell align="right" sx={TH_SX}>{t('statistical.recommendations.upside')}</TableCell>
                  <TableCell align="right" sx={TH_SX}>{t('statistical.recommendations.riskReward')}</TableCell>
                  <TableCell align="right" sx={TH_SX}>{t('statistical.recommendations.entryRisk')}</TableCell>
                  <TableCell align="right" sx={TH_SX}>{t('statistical.recommendations.rvol')}</TableCell>
                  <TableCell sx={TH_SX}>{t('statistical.recommendations.catalysts')}</TableCell>
                  <TableCell sx={TH_SX}>{t('statistical.recommendations.reason')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recData.slice(0, 30).map((r, idx) => {
                  const score = r.conviction_score || 0;
                  const action = r.conviction_action || '';
                  const tierClr = action === 'buy' ? COLORS.success : score >= 48 ? COLORS.blue : COLORS.warning;
                  const actionClr = action === 'buy' ? COLORS.success : score >= 48 ? COLORS.blue : COLORS.warning;
                  const lang = i18n.language?.startsWith('vi') ? 'vi' : 'en';
                  const label = typeof r.conviction_label === 'object' ? (r.conviction_label?.[lang] || r.conviction_label?.en || '') : (r.conviction_label || '');
                  const reason = typeof r.conviction_reason === 'object' ? (r.conviction_reason?.[lang] || r.conviction_reason?.en || '') : (r.conviction_reason || '');
                  const catalysts = r.catalysts || [];
                  const CATALYST_CLR = {
                    vol_spring: '#8b5cf6', pocket_pivot: '#3b82f6', flow_accel: '#06b6d4',
                    breakout: '#10b981', rs_breakout: '#10b981', gap_up: '#22c55e',
                    vol_dryup: '#f59e0b', accumulation: '#6366f1', reversal: '#ef4444',
                  };

                  return (
                    <TableRow key={r.ticker} hover sx={{ bgcolor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <TableCell sx={{ ...TD_SX, fontWeight: 700, color: COLORS.title, position: 'sticky', left: 0, bgcolor: idx % 2 === 0 ? '#fff' : '#fafafa', zIndex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: tierClr, flexShrink: 0 }} />
                          <Box>
                            {r.ticker}
                            {r.company_name && <Typography sx={{ fontSize: '0.55rem', color: COLORS.subtitle, fontWeight: 400, lineHeight: 1.2, mt: 0.2 }}>{r.company_name}</Typography>}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={TD_SX}>
                        <Chip size="small" label={label}
                          sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, borderRadius: '6px',
                            bgcolor: `${actionClr}14`, color: actionClr, border: `1px solid ${actionClr}33` }} />
                      </TableCell>
                      <TableCell align="right" sx={{ ...TD_SX, fontWeight: 800, color: tierClr }}>
                        {score.toFixed(0)}
                      </TableCell>
                      <TableCell align="right" sx={{ ...TD_SX, fontWeight: 600, color: r.calibrated_win_prob >= 0.55 ? COLORS.success : r.calibrated_win_prob >= 0.45 ? COLORS.warning : COLORS.danger }}>
                        {r.calibrated_win_prob != null ? `${(r.calibrated_win_prob * 100).toFixed(0)}%` : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ ...TD_SX, fontWeight: 700, color: r.upside_pct >= 10 ? COLORS.success : r.upside_pct >= 5 ? COLORS.blue : COLORS.textSecondary }}>
                        {r.upside_pct != null ? `+${r.upside_pct.toFixed(1)}%` : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ ...TD_SX, fontWeight: 700, color: r.risk_reward >= 1.5 ? COLORS.success : r.risk_reward >= 1.0 ? COLORS.warning : COLORS.danger }}>
                        {r.risk_reward != null ? `${r.risk_reward.toFixed(1)}x` : '—'}
                      </TableCell>
                      <TableCell align="right" sx={TD_SX}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Box sx={{ width: 28, height: 4, borderRadius: 2, bgcolor: '#e2e8f0' }}>
                            <Box sx={{ width: `${Math.min(100, r.entry_risk || 0)}%`, height: '100%', borderRadius: 2,
                              bgcolor: r.entry_risk >= 70 ? COLORS.success : r.entry_risk >= 40 ? COLORS.warning : COLORS.danger }} />
                          </Box>
                          <Typography sx={{ fontSize: '0.75rem' }}>{r.entry_risk?.toFixed(0) || '—'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ ...TD_SX, fontWeight: 600, color: r.rvol >= 2 ? COLORS.accent : r.rvol >= 1.5 ? COLORS.blue : COLORS.textSecondary }}>
                        {r.rvol != null ? `${r.rvol.toFixed(1)}x` : '—'}
                      </TableCell>
                      <TableCell sx={TD_SX}>
                        <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap' }}>
                          {catalysts.slice(0, 4).map((c) => (
                            <Chip key={c} size="small" label={c.replace(/_/g, ' ')}
                              sx={{ height: 18, fontSize: '0.58rem', fontWeight: 600, borderRadius: '4px',
                                bgcolor: `${CATALYST_CLR[c] || COLORS.textSecondary}14`,
                                color: CATALYST_CLR[c] || COLORS.textSecondary }} />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...TD_SX, minWidth: 280 }}>
                        <Typography sx={{ fontSize: '0.7rem', color: COLORS.textSecondary, whiteSpace: 'normal', lineHeight: 1.4 }}>
                          {reason}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ═══ Tabs ═══ */}
      <Paper elevation={0} sx={{ borderRadius: 1.5, border: `1px solid ${COLORS.cardBorder}`, mb: 2.5, overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{
            minHeight: 44, px: 1,
            '& .MuiTab-root': {
              minHeight: 44, fontSize: '0.78rem', fontWeight: 600, textTransform: 'none',
              color: COLORS.textSecondary, letterSpacing: 0,
              '&.Mui-selected': { color: COLORS.accent, fontWeight: 700 },
            },
            '& .MuiTabs-indicator': { height: 2.5, borderRadius: '2px 2px 0 0', bgcolor: COLORS.accent },
          }}>
          <Tab icon={<ChartIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t('statistical.tabs.garch')} />
          <Tab icon={<SecurityIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t('statistical.tabs.var')} />
          <Tab icon={<PieIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t('statistical.tabs.markowitz')} />
          <Tab icon={<TrendIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t('statistical.tabs.calibration')} />
        </Tabs>
      </Paper>

      {/* ═══ Tab content ═══ */}
      {tab === 0 && <GarchTab t={t} tickers={tickers} loading={false} garchData={garchData} garchLoading={garchLoading} onRefresh={garchScan} nameMap={nameMap} />}
      {tab === 1 && <VarTab t={t} tickers={tickers} loading={false} varData={varData} varLoading={varLoading} onRefresh={varScan} nameMap={nameMap} />}
      {tab === 2 && <MarkowitzTab t={t} tickers={tickers} loading={false} onMetrics={setMarkowitzMetrics} />}
      {tab === 3 && <CalibrationTab t={t} overview={calOverview} calLoading={calLoading} />}
    </Box>
  );
};

export default StatisticalDashboard;
